'use client';

import { useState } from 'react';
import Link from 'next/link';
import {
  Search,
  Loader2,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  ArrowRight,
  Send,
} from 'lucide-react';
import type { ScanResult, Severity, Verdict } from '@/lib/web3/types';
import { LINK_GUARD_BOT_URL } from '@/lib/silkai';

const EXAMPLES = [
  { label: 'jup.ag', value: 'https://jup.ag' },
  { label: 'jupiter-airdrop.xyz', value: 'https://jupiter-airdrop.xyz/claim' },
  { label: 'phantom-wallet.click', value: 'https://connect.phantom-wallet.click' },
];

const VERDICT_STYLES: Record<
  Verdict,
  { label: string; ring: string; text: string; bg: string; Icon: typeof ShieldCheck }
> = {
  safe: {
    label: 'Looks safe',
    ring: 'border-emerald-500/40',
    text: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    Icon: ShieldCheck,
  },
  caution: {
    label: 'Be careful',
    ring: 'border-amber-500/40',
    text: 'text-amber-400',
    bg: 'bg-amber-500/10',
    Icon: ShieldAlert,
  },
  danger: {
    label: 'Danger - likely drainer',
    ring: 'border-rose-500/50',
    text: 'text-rose-400',
    bg: 'bg-rose-500/10',
    Icon: ShieldX,
  },
};

const SEVERITY_DOT: Record<Severity, string> = {
  info: 'bg-neutral-500',
  low: 'bg-amber-400/70',
  medium: 'bg-amber-400',
  high: 'bg-rose-400',
  critical: 'bg-rose-500',
};

export default function Scanner() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<ScanResult | null>(null);

  async function runScan(target: string) {
    const value = target.trim();
    if (!value) {
      setError('Paste a Solana dApp link first.');
      return;
    }
    setError('');
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch('/api/link-guard/scan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: value }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || 'Scan failed. Try again.');
        return;
      }
      setResult(data as ScanResult);
    } catch {
      setError('Network error. Try again.');
    } finally {
      setLoading(false);
    }
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    void runScan(url);
  }

  const style = result ? VERDICT_STYLES[result.verdict] : null;

  return (
    <div className="relative rounded-none border border-white/10 bg-neutral-900/40 p-6 sm:p-10">
      <form onSubmit={onSubmit} className="max-w-2xl mx-auto">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://some-solana-dapp.xyz/claim"
              className="w-full h-12 pl-11 pr-4 rounded-none bg-black/50 border border-white/10 text-white text-sm placeholder:text-neutral-600 focus:outline-none focus:border-rose-500/50 transition-colors"
              aria-label="Solana dApp link to scan"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center h-12 px-7 rounded-none bg-white text-black text-sm font-semibold hover:bg-neutral-200 transition-colors gap-2 disabled:opacity-60 shrink-0"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Scan link <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-neutral-600">Try:</span>
          {EXAMPLES.map((ex) => (
            <button
              key={ex.value}
              type="button"
              onClick={() => {
                setUrl(ex.value);
                void runScan(ex.value);
              }}
              className="text-xs font-mono text-neutral-400 border border-white/10 px-2 py-1 hover:border-rose-500/40 hover:text-white transition-colors"
            >
              {ex.label}
            </button>
          ))}
        </div>

        {error && <p className="mt-3 text-sm text-rose-400">{error}</p>}
      </form>

      {result && style && (
        <div className="mt-8 max-w-2xl mx-auto">
          <div className={`rounded-none border ${style.ring} ${style.bg} p-5`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <style.Icon className={`w-7 h-7 ${style.text}`} />
                <div>
                  <p className={`text-lg font-semibold ${style.text}`}>{style.label}</p>
                  {result.host && (
                    <p className="text-xs font-mono text-neutral-400 break-all">{result.host}</p>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className={`text-3xl font-bold tabular-nums ${style.text}`}>{result.score}</p>
                <p className="text-[10px] uppercase tracking-wider text-neutral-500">risk / 100</p>
              </div>
            </div>

            <div className="mt-4 h-1.5 w-full bg-black/40 overflow-hidden">
              <div
                className={`h-full ${
                  result.verdict === 'danger'
                    ? 'bg-rose-500'
                    : result.verdict === 'caution'
                      ? 'bg-amber-400'
                      : 'bg-emerald-500'
                }`}
                style={{ width: `${result.score}%` }}
              />
            </div>
          </div>

          <ul className="mt-4 space-y-2">
            {result.signals.map((s, i) => (
              <li
                key={`${s.id}-${i}`}
                className="flex gap-3 border border-white/8 bg-black/30 p-3"
              >
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEVERITY_DOT[s.severity]}`} />
                <div>
                  <p className="text-sm font-medium text-white">{s.title}</p>
                  <p className="text-xs text-neutral-400">{s.detail}</p>
                </div>
              </li>
            ))}
          </ul>

          {result.verdict !== 'safe' && (
            <div className="mt-4 border border-rose-500/20 bg-rose-500/5 p-4 text-sm text-neutral-300">
              Never approve a transaction or sign a message you do not fully understand.
              Revoke approvals if you already connected your wallet.
            </div>
          )}

          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <a
              href={LINK_GUARD_BOT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-none border border-white/15 text-sm text-white hover:border-white/40 transition-colors"
            >
              <Send className="w-4 h-4" /> Check links in Telegram
            </a>
            <Link
              href="/web3"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-none bg-rose-500/90 text-sm font-semibold text-white hover:bg-rose-500 transition-colors"
            >
              More Web3 tools <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <p className="mt-4 text-xs text-neutral-600">
            Heuristic analysis - not financial or security advice. A &quot;safe&quot; result is
            not a guarantee. Always verify the official domain yourself.
          </p>
        </div>
      )}
    </div>
  );
}
